'use client';
import { useState, type ReactNode } from 'react';
import { ManageSelectionModal, type ManageSelectionItem, type ManageSelectionOutcome } from './ManageSelectionModal';
import { SelectionSummary } from './SelectionSummary';
import { ModelIcon } from './ModelIcon';
import { interpolate, useTranslation } from '../../lib/i18n';

/** The ONE control for choosing several models: a {@link SelectionSummary} card on the page (a count line,
 *  one brand mark per pick, a "Manage" button) opening a {@link ManageSelectionModal} window
 *  (switch, search, kind dropdown, provider tabs, grouped rows, Cancel and Save). The provider dialog, the
 *  account allowed-models grants and presets, Fast mode and the plugin `models` field all render this, so a
 *  model selection looks and behaves the same wherever it appears. Single-model choices are the anchored
 *  dropdown {@link RowPicker}, not this.
 *
 *  `items` carry the provider groups and brand marks (`brainModelSelection` builds them from the catalog, or
 *  `modelPickerItems` from a probed endpoint); a stored id the catalog no longer offers is the caller's job
 *  to pin into `items`, so a save never silently drops it. `emptyText` says what an empty selection means
 *  for the caller ("no model allowed", "listed automatically") in the card and in the window's footer. An
 *  `allOption` is an exclusive "everything" switch above the list: while it is on the stored selection is
 *  exactly `{ id }`, and `allText` is the card's line then. `disabled` is for a record whose catalog is
 *  empty: the card stays so the record keeps its shape, and the caller explains why beside it.
 *
 *  `onChange` is awaited by the window: return the real promise and let it reject. `failureMessage` and `reload`
 *  are the window's own (see {@link ManageSelectionOutcome}) and are passed straight through: a lost connection
 *  offers the caller's `reload`, a curated refusal shows the caller's text, and nothing here resends the save. */
export function ModelSelection({ title, hint, manageAriaLabel, items, groupIcons, value, onChange, saving = false, disabled = false, flat = false, emptyText, allOption, allText, kindFilter, failureMessage, reload }: {
  /** Title of the window. */
  title: string;
  /** The window's subtitle: what the selection means. */
  hint?: string;
  /** Accessible name of the Manage button when several selections share one page. */
  manageAriaLabel?: string;
  items: ManageSelectionItem[];
  groupIcons?: Record<string, ReactNode>;
  value: string[];
  onChange: (next: string[]) => void | Promise<void>;
  saving?: boolean;
  disabled?: boolean;
  /** The rows are not grouped by provider (a probed endpoint's own list), so the card counts models only. */
  flat?: boolean;
  emptyText: string;
  allOption?: { id: string; label: string; description?: string };
  /** The card's count line while `allOption` is on. */
  allText?: (available: number) => string;
  kindFilter?: { options: { value: string; label: string }[]; label: string };
} & ManageSelectionOutcome) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const all = allOption !== undefined && value.length === 1 && value[0] === allOption.id;
  const byId = new Map(items.map((item) => [item.id, item]));
  // Stored ids stay in the card because they are still stored, even when `items` no longer lists them.
  const named = all ? items.map((item) => item.id) : value;
  const providers = new Set(value.flatMap((id) => {
    const group = byId.get(id)?.group;
    return group ? [group] : [];
  }));
  const countText = all && allText ? allText(items.length)
    : value.length === 0 ? emptyText
      : flat ? interpolate(t.managePicker.modelsSelected, { n: String(value.length) })
        : interpolate(t.managePicker.modelsCount, { n: String(value.length), p: String(providers.size) });
  return (
    <>
      <SelectionSummary
        countText={countText}
        samples={[]}
        moreCount={0}
        marks={{ kind: 'models', marks: named.map((id) => {
          const label = byId.get(id)?.label ?? id;
          return { label, node: <ModelIcon name={label} size={12} /> };
        }) }}
        onManage={() => setOpen(true)}
        manageLabel={t.managePicker.manage}
        manageAriaLabel={manageAriaLabel}
        disabled={disabled}
      />
      <ManageSelectionModal
        title={title}
        subtitle={hint}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        groupIcons={groupIcons}
        kindFilter={kindFilter}
        selected={new Set(value)}
        onSave={(next) => onChange([...next])}
        saving={saving}
        failureMessage={failureMessage}
        reload={reload}
        emptySelectionHint={emptyText}
        allOption={allOption}
        countLabel={(n) => interpolate(t.managePicker.modelsSelected, { n: String(n) })}
      />
    </>
  );
}
